import React, { useEffect, useState } from "react";

import type { EditorProps, Monaco, OnMount } from "@monaco-editor/react";

import { LoadingSpinner } from "@braze/beacon-core";

import { getLoadedMonaco, loadMonaco } from "./loadMonaco";
import type { LoadedMonaco } from "./types";

const useLoadedMonaco = (): LoadedMonaco | undefined => {
  const [loaded, setLoaded] = useState(getLoadedMonaco);

  useEffect(() => {
    if (loaded) {
      return undefined;
    }
    let mounted = true;
    loadMonaco().then(
      (result) => {
        if (mounted) {
          setLoaded(result);
        }
      },
      (error: unknown) => {
        // Matches @monaco-editor/react: log and stay unloaded. The next mount retries.
        console.error("Monaco failed to load", error);
      }
    );
    return () => {
      mounted = false;
    };
  }, [loaded]);

  return loaded;
};

/** Same contract as `useMonaco` from @monaco-editor/react: null until Monaco is ready. */
const useMonaco = (): Monaco | null => useLoadedMonaco()?.monaco ?? null;

/** @monaco-editor/react's `Editor`. Its loading content carries `data-monaco-loading` until the editor exists. */
const Editor = (props: EditorProps) => {
  const loaded = useLoadedMonaco();
  const loading = (
    <div data-monaco-loading="" style={{ display: "contents" }}>
      {props.loading ?? <LoadingSpinner />}
    </div>
  );
  if (loaded) {
    return <loaded.Editor {...props} loading={loading} />;
  }
  // Same wrapper and loading markup that @monaco-editor/react renders before the editor mounts
  const { width = "100%", height = "100%", wrapperProps } = props;
  return (
    <section
      style={{ display: "flex", position: "relative", textAlign: "initial", width, height }}
      {...wrapperProps}
    >
      <div
        style={{ display: "flex", height: "100%", width: "100%", justifyContent: "center", alignItems: "center" }}
      >
        {loading}
      </div>
    </section>
  );
};

export { Editor, useMonaco };
export type { EditorProps, Monaco, OnMount };
